import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  formatCurrency,
  formatPercent,
  formatUnitPriceBreakdown,
} from '@/utils/money';
import { formatDate } from '@/utils/date';
import { getAbbreviatedPackUnit } from '@/utils/item';
import { gql } from '@apollo/client';
import { PackUnit } from '@/enums';
import {
  SalesRep_VariantPackPurchaseHistoryQuery,
  SalesRep_VariantPackPurchaseHistoryQueryVariables,
} from '@/types';
import { Text, View } from 'react-native';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const VARIANT_PACK_PURCHASE_HISTORY_QUERY = gql`
  query SalesRep_VariantPackPurchaseHistory(
    $chatUUID: uuid!
    $limit: Int
    $variantPackUUID: uuid!
  ) {
    getVariantPackPurchaseHistory(
      chat_id: $chatUUID
      limit: $limit
      variant_pack_id: $variantPackUUID
    ) {
      base_quantity
      base_unit
      base_unit_price_in_micros
      order_date
      price_in_micros
      quantity
      sales_rep_gross_profit_in_micros
      sales_rep_gross_profit_in_percentage
      total_price_in_micros
      unit
      unit_count
    }
  }
`;

interface Props {
  chatUUID: string;
  currencyCode: string | null;
  variantPackUUID: string;
}

export const SalesRepPurchaseHistoryInfo: React.FC<Props> = ({
  chatUUID,
  variantPackUUID,
  currencyCode,
}) => {
  const [styles] = useStyles();
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );

  const { data, error, loading } = useWrappedQuery<
    SalesRep_VariantPackPurchaseHistoryQuery,
    SalesRep_VariantPackPurchaseHistoryQueryVariables
  >(VARIANT_PACK_PURCHASE_HISTORY_QUERY, {
    variables: {
      chatUUID,
      limit: 5,
      variantPackUUID,
    },
  });

  const purchaseHistoryData = useMemo(
    () => data?.getVariantPackPurchaseHistory || [],
    [data?.getVariantPackPurchaseHistory],
  );

  const noValueText = <Text style={TYPOGRAPHY.small}>--</Text>;
  return loading ? (
    <View style={styles.loadingContainer}>
      <ActivityIndicator accessibilityLabel="loading-indicator" />
    </View>
  ) : error || purchaseHistoryData.length === 0 ? (
    <View style={styles.emptyContainer}>
      <Text style={[TYPOGRAPHY.small, styles.emptyText]}>
        {i18n.t('no_order_history')}
      </Text>
    </View>
  ) : (
    <View
      accessibilityLabel="sales-rep-purchase-history-info"
      style={styles.base}
    >
      {purchaseHistoryData.map((purchase, index) => {
        // Display a per-unit price breakdown, e.g., 80.46 × $1.60/lb
        const perUnitPriceDisplay =
          typeof purchase.base_unit_price_in_micros === 'number' &&
          typeof purchase.base_quantity === 'number' &&
          purchase.base_unit
            ? formatUnitPriceBreakdown({
                currencyCode,
                quantity: purchase.base_quantity,
                unit: getAbbreviatedPackUnit(purchase.base_unit as PackUnit),
                unitPriceInMicros: purchase.base_unit_price_in_micros,
              })
            : null;

        // Since sales_rep_gross_profit_in_micros is per-unit; multiply by quantity for total
        // Divide percentage by 100 because backend returns whole number (e.g., 25 for 25%)
        // but formatPercent expects decimal (e.g., 0.25 for 25%)
        const totalGrossProfitDisplay =
          typeof purchase.sales_rep_gross_profit_in_micros === 'number' &&
          typeof purchase.sales_rep_gross_profit_in_percentage === 'number'
            ? `${formatCurrency(
                purchase.sales_rep_gross_profit_in_micros *
                  (purchase.quantity ?? 1),
                currencyCode,
              )} (${formatPercent(purchase.sales_rep_gross_profit_in_percentage / 100)})`
            : null;

        return (
          <View
            key={index}
            style={[
              styles.row,
              index !== purchaseHistoryData.length - 1
                ? styles.border
                : undefined,
            ]}
          >
            <View style={styles.column}>
              {typeof purchase.order_date === 'string' ? (
                <Text style={[TYPOGRAPHY.small, FONTS.primarySemiBold]}>
                  {formatDate(purchase.order_date)}
                </Text>
              ) : (
                noValueText
              )}
              {typeof purchase.quantity === 'number' ? (
                <Text style={[TYPOGRAPHY.small, styles.unitText]}>
                  {purchase.quantity}{' '}
                  {LOCALIZED_PACK_UNITS[purchase.unit as PackUnit] ||
                    purchase.unit}
                </Text>
              ) : (
                noValueText
              )}
            </View>
            <View style={styles.column}>
              {typeof purchase.total_price_in_micros === 'number' ? (
                <Text style={TYPOGRAPHY.small}>
                  {formatCurrency(purchase.total_price_in_micros, currencyCode)}
                </Text>
              ) : (
                noValueText
              )}
            </View>
            <View style={styles.columnExtraLarge}>
              {perUnitPriceDisplay ? (
                <Text style={TYPOGRAPHY.small}>{perUnitPriceDisplay}</Text>
              ) : (
                noValueText
              )}
            </View>
            {showGrossProfit ? (
              <View style={styles.columnLarge}>
                {totalGrossProfitDisplay ? (
                  <Text style={TYPOGRAPHY.small}>
                    {totalGrossProfitDisplay}
                  </Text>
                ) : (
                  noValueText
                )}
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    paddingBottom: 4,
    paddingTop: 8,
  },
  border: {
    borderBottomWidth: 1,
    borderColor: theme.colors.divider,
  },
  column: {
    flex: 3.5,
  },
  columnExtraLarge: {
    flex: 4.5,
  },
  columnLarge: {
    flex: 4,
  },
  emptyContainer: {
    padding: 16,
  },
  emptyText: {
    color: theme.colors.inactiveText,
    lineHeight: 20,
    textAlign: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  row: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  unitText: {
    paddingTop: 12,
    textTransform: 'capitalize',
  },
}));
